import { Logger } from '../utils/Logger';

/**
 * How long a container download may go without a new byte before the loader
 * calls it failed.
 *
 * This is the loader's own decision, separate from how long layout or Image
 * are willing to wait for an asset (`ASSET_PATIENCE_MS`): layout proceeds
 * without a late child and places it when it lands, it never fails the load.
 *
 * It only covers the DOWNLOAD. Once every byte is in, what is left is parse
 * and decode (Draco, meshopt, KTX2, texture upload), which reports no progress
 * and scales with the device, so there is no deadline on it here: a model
 * that takes 15 s to decode on a slow phone is still delivered.
 */
export const DOWNLOAD_STALL_MS = 10_000;

/**
 * A loader's way of saying "bytes are still arriving": call it with the running
 * total each time more of the asset lands, and with the asset's full size when
 * that is known. Called with nothing it is a bare sign of life.
 */
export type DownloadProgress = (loaded?: number, total?: number) => void;

/**
 * Bytes arriving for any load that shares it. A scene folder asks for hundreds
 * of files at once, and the browser opens only a few connections to one host:
 * the rest wait in its queue and see no byte until a connection frees up. A
 * load that has received nothing yet is therefore not stalled while another
 * load beside it is still receiving; its clock restarts on every such byte.
 * A load that has started receiving keeps its own clock.
 */
export class DownloadActivity {
    private readonly waiting = new Set<() => void>();

    /** Some load sharing this activity received a byte. */
    beat(): void {
        for (const wake of [...this.waiting]) wake();
    }

    /** Call `wake` on every beat until the returned function is called. */
    listen(wake: () => void): () => void {
        this.waiting.add(wake);
        return () => { this.waiting.delete(wake); };
    }
}

/** What a give-up calls the asset when its own `describe()` threw. */
const UNDESCRIBED_ASSET = 'An asset';

/**
 * Fail a load whose download has stalled: nothing arrived, or the reported
 * size says bytes are still missing, and no new byte for
 * {@link DOWNLOAD_STALL_MS}. Any new byte, or a larger size, restarts the
 * clock.
 *
 * Why it exists: Babylon's `RequestFile` unregisters its `readystatechange`
 * listener from inside the `loadend` handler (`fileTools.pure.js`), and on a
 * runtime where `loadend` fires before the readyState-4 `readystatechange`
 * (happy-dom, for a network-level failure with no HTTP response) neither
 * `onSuccess` nor `onError` is called. A dropped address then never answers,
 * and everything awaiting `LoadAssetContainerAsync` (Model, every Renderer
 * material slot) waits for good.
 *
 * When the download is NOT known to be incomplete the clock is off:
 * - every reported byte is in (loaded == total): the load is decoding;
 * - bytes arrived but there is no usable size (no `Content-Length`, or a
 *   gzip response whose compressed size the decoded count runs past): silence
 *   there cannot be told apart from decoding, so it is not called a stall.
 * A later request that grows the total (a split `.gltf` asking for its `.bin`
 * after the JSON lands) turns the clock back on.
 *
 * Limitation: a split `.gltf` whose `.bin` request never sends a single
 * progress event is not caught, and neither is a decoder that never answers.
 * Neither has been seen; if one is, it needs its own processing policy, not
 * this window.
 *
 * Loads that share an `activity` (one scene's files) wait for each other: a
 * load with no byte yet is not given up on while one beside it is receiving.
 *
 * A load that settles on its own is never touched. A load given up on that
 * later resolves is handed to `onAbandoned`, so the caller can free it.
 */
export function withDownloadStall<T>(
    load: (progress: DownloadProgress) => Promise<T>,
    describe: () => string,
    onAbandoned?: (late: T) => void,
    activity?: DownloadActivity,
): Promise<T> {
    return new Promise<T>((resolve, reject) => {
        let settled = false;
        let loaded = 0;
        let total: number | undefined;
        let timer: ReturnType<typeof setTimeout> | null = null;

        // Bytes are known to be missing: none yet, or fewer than the size.
        const incomplete = (): boolean => loaded === 0 || (total !== undefined && loaded < total);

        // Queued, not stalled, while nothing of ours has arrived and a load
        // beside us is still receiving (see DownloadActivity).
        let unlisten = activity?.listen(() => { if (loaded === 0 && timer !== null) restartClock(); });

        const giveUp = (): void => {
            if (settled) return;
            settled = true;
            timer = null;
            unlisten?.();
            const seconds = DOWNLOAD_STALL_MS / 1000;
            // Settled is already set, so a describe() that throws must still
            // end in the rejection below, or the load would never answer.
            let what: string;
            try {
                what = describe();
            } catch {
                what = UNDESCRIBED_ASSET;
            }
            const message = loaded > 0
                ? `${what} stopped arriving after ${megabytes(loaded)}`
                    + `${total !== undefined ? ` of ${megabytes(total)}` : ''}, nothing for ${seconds}s — treating as failed.`
                : `${what} sent nothing in ${seconds}s — treating as failed.`;
            Logger.warn(`[DownloadStall] ${message}`);
            reject(new Error(message));
        };

        const stopClock = (): void => {
            if (timer !== null) clearTimeout(timer);
            timer = null;
        };
        const restartClock = (): void => {
            stopClock();
            timer = setTimeout(giveUp, DOWNLOAD_STALL_MS);
        };

        restartClock();
        const progress: DownloadProgress = (bytes, size) => {
            if (settled) return;
            const moreBytes = bytes !== undefined && bytes > loaded;
            const largerSize = size !== undefined && size > (total ?? 0);
            if (moreBytes) {
                loaded = bytes;
                unlisten?.();
                unlisten = undefined;
                activity?.beat();
            }
            if (size !== undefined && size > 0) total = size;

            if (!incomplete()) { stopClock(); return; }
            // A repeated total is a stalled connection, not a live one. A bare
            // signal (no byte count) is a sign of life.
            if (bytes !== undefined && !moreBytes && !largerSize) return;
            restartClock();
        };

        const fail = (error: unknown): void => {
            if (settled) return;
            settled = true;
            stopClock();
            unlisten?.();
            reject(error);
        };

        // A load that throws before it returns a promise must not leave the
        // clock running: it would fire later and log a give-up for a load that
        // already failed.
        let promise: Promise<T>;
        try {
            promise = load(progress);
        } catch (error) {
            fail(error);
            return;
        }
        promise.then(
            (value) => {
                if (settled) {
                    onAbandoned?.(value);
                    return;
                }
                settled = true;
                stopClock();
                unlisten?.();
                resolve(value);
            },
            fail,
        );
    });
}

/** "6.3 MB", or "80 KB" below a tenth of one. */
function megabytes(bytes: number): string {
    return bytes >= 100_000 ? `${(bytes / 1e6).toFixed(1)} MB` : `${Math.round(bytes / 1e3)} KB`;
}
